import {
  CLASSES,
  type Equipped,
  effectiveDps,
  equipmentBonus,
  favouritePrey,
  getItem,
  lookTints,
  type AchievementsEvent,
  type MobDefinition,
  MobIdSchema,
  MOBS,
  type ProfileEvent,
  type ProgressEvent,
  statsFor,
  totalKills,
  wornPieces,
} from '@shefira/shared';

import { type CssAtlas, type DomAtlas, loadCharacterAtlas } from '../assets/dom-atlas.js';
import type { StoredSession } from '../net/session.js';
import { AccountTab } from './account-tab.js';
import { AchievementsTab, claimBadge } from './achievements-tab.js';
import { DollPreview } from './doll-preview.js';
import { HEAD_FRAME } from './portrait.js';
import { isAction } from '../settings/keybindings.js';
import { combatOfEquipped } from './combat-lines.js';
import { closeButton, hudShortcutButton } from './hud.js';
import { formatDps } from './item-tooltip.js';
import { formatCount, formatDate, formatDuration } from './profile-format.js';
import { element, kitSeal, kitStage } from './kit.js';
import { chips, hint, huntingRecord, lines, statCards, xpBar } from './profile-sections.js';
import { uiFormat, uiText } from './text.js';
import './profile.css';

/**
 * Where the head sits on the 32×32 character canvas, facing south. The body
 * templates are frozen (CLAUDE.md rule 9) and the classes share the head, so
 * a fixed box frames it: hair and helmet included, shoulders just cut off.
 */
const HEAD = HEAD_FRAME;

/** Rows of the hunting record: the prey most hunted, not the whole bestiary. */
const PREY_SHOWN = 5;

export interface ProfilePanelOptions {
  /** Item icons, for an achievement's reward. */
  icons: () => CssAtlas | null;
  session: StoredSession;
  /** Portals the zone has, for the « found / total » count. */
  portalCount: number;
  /** Ask the server for the career; its answer comes back through `setProfile`. */
  onRequest: () => void;
  /** Ask for the achievements (ADR 0046); the answer comes back through `setAchievements`. */
  onAchievements: () => void;
  onWearTitle: (achievementId: string | null) => void;
  onClaimReward: (achievementId: string) => void;
}

type ProfileTab = 'profile' | 'achievements' | 'account';

function mobOf(id: string): MobDefinition | undefined {
  const parsed = MobIdSchema.safeParse(id);
  return parsed.success ? MOBS[parsed.data] : undefined;
}

/**
 * The player's profile, opened by the portrait in the top row or P: a window
 * in the middle of the screen with who they are (dressed portrait, level,
 * the three stats), what they have done (the career the server counts) and,
 * on a second tab, their account.
 *
 * Nothing here is decided: the stats are the same derivation the inventory
 * shows, and the career is displayed as the server sent it.
 */
export class ProfilePanel {
  private readonly root: HTMLElement;
  private readonly button: HTMLButtonElement;
  private readonly body: HTMLElement;
  private readonly account: AccountTab;
  private readonly achievements: AchievementsTab;
  private readonly achievementsButton: HTMLButtonElement;
  private readonly achievementsTabSlot = element('span', 'profile__tab-slot');
  private readonly tabs = new Map<ProfileTab, HTMLButtonElement>();
  private tab: ProfileTab = 'profile';
  private headPortrait?: { doll: DollPreview; canvas: HTMLCanvasElement };
  private fullPortrait?: DollPreview;
  private equipped: Equipped = {};
  private progress?: ProgressEvent;
  private gold = 0;
  private portalsFound = 0;
  private profile?: ProfileEvent;
  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.metaKey || event.ctrlKey || event.altKey || event.repeat) return;
    if (document.activeElement instanceof HTMLInputElement) return;
    const key = event.key.toLowerCase();
    if (key === 'escape' && !this.root.hidden) this.toggle(false);
    else if (isAction(event, 'profile')) this.toggleOn('profile');
    else if (isAction(event, 'achievements')) this.toggleOn('achievements');
  };

  constructor(private readonly options: ProfilePanelOptions) {
    this.root = document.createElement('section');
    this.root.className = 'profile';
    this.root.hidden = true;
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-modal', 'true');
    this.root.setAttribute('aria-label', uiText('Profil'));

    const frame = document.createElement('div');
    frame.className = 'profile__window';
    const tabBar = document.createElement('div');
    tabBar.className = 'login__tabs profile__tabs';
    for (const [id, label] of [
      ['profile', 'Profil'],
      ['achievements', 'Succès'],
      ['account', 'Compte'],
    ] as const) {
      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'login__tab';
      tab.textContent = uiText(label);
      tab.addEventListener('click', () => {
        this.showTab(id);
      });
      this.tabs.set(id, tab);
      // A tab clips its label and dims when not shown: a badge sits on its slot, over its
      // corner. Every tab has one, or the slotted tab would share the row unevenly.
      const slot =
        id === 'achievements' ? this.achievementsTabSlot : element('span', 'profile__tab-slot');
      slot.append(tab);
      tabBar.append(slot);
    }
    this.body = document.createElement('div');
    this.body.className = 'profile__tab-body';
    this.account = new AccountTab(options.session);
    this.achievements = new AchievementsTab({
      onWear: options.onWearTitle,
      onClaim: options.onClaimReward,
      icons: options.icons,
    });
    frame.append(
      tabBar,
      this.body,
      this.achievements.element,
      this.account.element,
      closeButton('Fermer (Échap)', () => {
        this.toggle(false);
      }),
    );
    this.root.append(frame);
    // A click on the veil around the window closes it, like Escape.
    this.root.addEventListener('click', (event) => {
      if (event.target === this.root) this.toggle(false);
    });
    document.body.append(this.root);
    window.addEventListener('keydown', this.onKeyDown);
    this.showTab('profile');

    this.button = hudShortcutButton('account', 'Profil', 'profile', 'top');
    this.button.addEventListener('click', () => {
      this.toggleOn('profile');
    });
    this.achievementsButton = hudShortcutButton('achievements', 'Succès', 'achievements', 'top');
    this.achievementsButton.addEventListener('click', () => {
      this.toggleOn('achievements');
    });
    void loadCharacterAtlas().then((atlas) => {
      this.mountPortraits(atlas);
    });
  }

  /** The worn pieces changed: the portraits and the stats follow. */
  setEquipped(equipped: Equipped): void {
    this.equipped = equipped;
    this.drawPortraits();
    this.render();
  }

  setProgress(progress: ProgressEvent): void {
    this.progress = progress;
    this.render();
  }

  setGold(gold: number): void {
    this.gold = gold;
    this.render();
  }

  setPortalsFound(count: number): void {
    this.portalsFound = count;
    this.render();
  }

  setProfile(profile: ProfileEvent): void {
    this.profile = profile;
    this.render();
  }

  setAchievements(state: AchievementsEvent): void {
    this.achievements.setState(state);
    // A reward waiting (the beta's Glitch, the Five Lands' blade) shows on the HUD button and the tab.
    const waiting = this.achievements.claimable();
    for (const host of [this.achievementsButton, this.achievementsTabSlot]) {
      host.querySelector(':scope > .achievements__badge')?.remove();
      if (waiting > 0) host.append(claimBadge(waiting));
    }
  }

  toggle(open = this.root.hidden): void {
    this.root.hidden = !open;
    // Asked afresh at every opening: the career moves with every blow.
    if (open) this.options.onRequest();
    if (open) this.options.onAchievements();
  }

  /** Open on a tab, or close when it is already the one shown. */
  private toggleOn(tab: ProfileTab): void {
    if (!this.root.hidden && this.tab === tab) {
      this.toggle(false);
      return;
    }
    this.showTab(tab);
    if (this.root.hidden) this.toggle(true);
  }

  /** Open on the account tab: where a guest adds an e-mail (the shop sends them here, ADR 0044). */
  openAccount(): void {
    this.toggle(true);
    this.showTab('account');
  }

  private showTab(id: ProfileTab): void {
    this.tab = id;
    for (const [tabId, tab] of this.tabs) {
      tab.classList.toggle('login__tab--active', tabId === id);
    }
    this.body.hidden = id !== 'profile';
    this.achievements.element.hidden = id !== 'achievements';
    this.account.element.hidden = id !== 'account';
    if (id === 'account') void this.account.refresh();
  }

  /**
   * The HUD button shows the played character's head instead of the generic
   * icon, which stays as the fallback until the atlas arrives; the window
   * shows the whole character, dressed.
   */
  private mountPortraits(atlas: DomAtlas | null): void {
    const { character } = this.options.session;
    const prefix = atlas?.prefixOf(`body.${character.classId}`) ?? null;
    if (atlas === null || prefix === null || !this.button.isConnected) return;
    const doll = (scale: number, centred: boolean): DollPreview =>
      new DollPreview({
        bodyKey: prefix,
        frame: atlas.frame,
        featureTints: lookTints(character.look),
        hairStyle: character.look.hairStyle,
        body: character.look.body,
        scale,
        centred,
      });
    const canvas = document.createElement('canvas');
    canvas.className = 'hud-button__portrait';
    canvas.width = HEAD.size;
    canvas.height = HEAD.size;
    this.button.replaceChildren(canvas);
    this.headPortrait = { doll: doll(1, false), canvas };
    this.fullPortrait = doll(4, false);
    this.drawPortraits();
    this.render();
  }

  private drawPortraits(): void {
    this.fullPortrait?.draw(this.equipped);
    if (this.headPortrait === undefined) return;
    const { doll, canvas } = this.headPortrait;
    doll.draw(this.equipped);
    const context = canvas.getContext('2d');
    if (context === null) return;
    context.imageSmoothingEnabled = false;
    context.clearRect(0, 0, HEAD.size, HEAD.size);
    context.drawImage(
      doll.canvas,
      HEAD.x,
      HEAD.y,
      HEAD.size,
      HEAD.size,
      0,
      0,
      HEAD.size,
      HEAD.size,
    );
  }

  private render(): void {
    const { character } = this.options.session;
    const definition = CLASSES[character.classId];
    const level = this.progress?.level ?? character.level;
    const stats = statsFor(definition, level, equipmentBonus(wornPieces(this.equipped, getItem)));
    const career = this.profile?.career;

    const hero = document.createElement('div');
    hero.className = 'profile__hero';
    // The character stands on a patch of the real world (the welcome
    // backdrop), feet on a shadow: the portrait is a scene, not a slot.
    const stage = kitStage('profile__stage');
    if (this.fullPortrait !== undefined) stage.append(this.fullPortrait.canvas);
    const identity = document.createElement('div');
    identity.className = 'profile__identity';
    const title = document.createElement('div');
    title.className = 'profile__title';
    const names = document.createElement('div');
    names.className = 'profile__names';
    const name = document.createElement('h2');
    name.className = 'profile__name';
    name.textContent = uiText(character.name);
    const line = document.createElement('p');
    line.className = 'profile__class';
    line.textContent = uiText(definition.name);
    names.append(name, line);
    title.append(kitSeal(level), names);
    const footer = document.createElement('div');
    footer.className = 'profile__footer';
    footer.append(
      chips([
        { icon: 'gold', label: 'Or', value: formatCount(this.gold) },
        {
          icon: 'portal',
          label: 'Portails découverts',
          value: `${String(this.portalsFound)} / ${String(this.options.portalCount)}`,
        },
      ]),
    );
    const since = this.profile === undefined ? '' : formatDate(this.profile.createdAt);
    if (since !== '') footer.append(hint(uiFormat('Aventurier depuis le {date}', { date: since })));
    identity.append(
      title,
      xpBar(this.progress),
      statCards([
        { tone: 'health', icon: 'stat-health', label: 'Vie', value: formatCount(stats.maxHealth) },
        {
          tone: 'dps',
          icon: 'stat-dps',
          label: 'DPS',
          value: formatDps(effectiveDps(stats.dps, combatOfEquipped(this.equipped))),
        },
        {
          tone: 'defence',
          icon: 'stat-defence',
          label: 'Défense',
          value: formatCount(stats.defence),
        },
      ]),
      footer,
    );
    hero.append(stage, identity);

    const pending = '…';
    const count = (value: number | undefined): string =>
      value === undefined ? pending : formatCount(value);
    const bosses =
      career === undefined
        ? undefined
        : totalKills(career, 'zone-boss', mobOf) + totalKills(career, 'dungeon-boss', mobOf);
    const prey =
      this.profile === undefined
        ? undefined
        : favouritePrey(this.profile.career, PREY_SHOWN).map(({ mobId, count: kills }) => ({
            name: mobOf(mobId)?.name ?? mobId,
            count: kills,
          }));

    const columns = document.createElement('div');
    columns.className = 'profile__columns';
    columns.append(
      lines('Carrière', [
        {
          icon: 'stat-kills',
          label: 'Ennemis vaincus',
          value: count(career === undefined ? undefined : totalKills(career)),
        },
        { icon: 'stat-bosses', label: 'Boss vaincus', value: count(bosses) },
        { icon: 'stat-deaths', label: 'Chutes', value: count(career?.deaths) },
        { icon: 'stat-best-hit', label: 'Plus gros coup', value: count(career?.bestHit) },
        { icon: 'stat-damage', label: 'Dégâts infligés', value: count(career?.damageDealt) },
        { icon: 'stat-spells', label: 'Sorts lancés', value: count(career?.spellsCast) },
        { icon: 'stat-loot', label: 'Objets ramassés', value: count(career?.itemsLooted) },
        { icon: 'stat-steps', label: 'Cases parcourues', value: count(career?.tilesWalked) },
        {
          icon: 'stat-time',
          label: 'Temps de jeu',
          value: career === undefined ? pending : formatDuration(career.playSeconds),
        },
      ]),
      huntingRecord(prey),
    );

    this.body.replaceChildren(hero, columns);
  }

  destroy(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    this.button.remove();
    this.achievementsButton.remove();
    this.root.remove();
  }
}
